<script lang="ts">
  import { fade, blur } from 'svelte/transition';
  import { onMount } from 'svelte';
  import confetti from 'canvas-confetti';

  export let isVisible = false;
  
  let canvas: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D | null;
  let animationId: number;
  let showText = false;
  let glitchActive = false;
  
  const matrixChars = '01';
  const drops: number[] = [];

  const startMatrix = () => {
    if (!canvas || !ctx) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const fontSize = 16;
    const columns = canvas.width / fontSize;
    
    for (let i = 0; i < columns; i++) {
      drops[i] = 1;
    }
    
    const draw = () => {
      ctx!.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx!.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx!.fillStyle = '#00ff00';
      ctx!.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = matrixChars[Math.floor(Math.random() * matrixChars.length)];
        ctx!.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const animate = () => {
      draw();
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
  };

  const triggerGlitch = () => {
    glitchActive = true;
    setTimeout(() => {
      glitchActive = false;
    }, 2000);
  };

  const triggerNeonConfetti = () => {
    const neonColors = ['#00ff00', '#00ffff', '#ff00ff', '#ffff00'];
    
    // Neon burst
    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.5 },
      colors: neonColors,
      shapes: ['square'],
      scalar: 1.2
    });
  };

  onMount(() => {
    if (isVisible && canvas) {
      ctx = canvas.getContext('2d');
      startMatrix();
      
      setTimeout(() => {
        showText = true;
        triggerGlitch();
        triggerNeonConfetti();
      }, 1000);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  });
</script>

{#if isVisible}
  <div class="matrix-overlay" in:fade={{ duration: 800 }}>
    <canvas bind:this={canvas} class="matrix-canvas"></canvas>
    
    {#if showText}
      <div class="sold-out-content" class:glitch={glitchActive} in:blur={{ duration: 800 }}>
        <div class="neon-box">
          <h1 class="neon-text">SOLD OUT</h1>
          <div class="sub-text">
            <p>SYSTEM OVERLOAD</p>
            <p>MAXIMUM CAPACITY REACHED</p>
            <div class="loading-bar">
              <div class="loading-fill"></div>
            </div>
            <p class="status">STATUS: 100% ALLOCATED</p>
          </div>
        </div>
        
        <div class="scanlines"></div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .matrix-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: black;
    z-index: 10000;
    overflow: hidden;
  }

  .matrix-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .sold-out-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #00ff00;
    z-index: 10001;
  }

  .neon-box {
    border: 2px solid #00ff00;
    padding: 3rem 2rem;
    background: rgba(0, 0, 0, 0.8);
    box-shadow: 
      0 0 20px #00ff00,
      inset 0 0 20px rgba(0, 255, 0, 0.1);
    position: relative;
    animation: neonPulse 2s ease-in-out infinite alternate;
  }

  .neon-text {
    font-size: 4rem;
    font-weight: 900;
    margin: 0 0 1rem 0;
    font-family: 'Courier New', monospace;
    text-shadow: 
      0 0 5px #00ff00,
      0 0 10px #00ff00,
      0 0 20px #00ff00,
      0 0 40px #00ff00;
    animation: textFlicker 0.15s infinite linear alternate;
  }

  .sub-text {
    font-family: 'Courier New', monospace;
    font-size: 1rem;
  }

  .sub-text p {
    margin: 0.5rem 0;
    text-shadow: 0 0 10px #00ff00;
  }

  .loading-bar {
    width: 100%;
    height: 10px;
    border: 1px solid #00ff00;
    margin: 1rem 0;
    background: black;
    overflow: hidden;
  }

  .loading-fill {
    height: 100%;
    background: linear-gradient(90deg, transparent, #00ff00, transparent);
    animation: loadingAnimation 2s ease-in-out infinite;
  }

  .status {
    font-size: 0.9rem;
    color: #00ffff;
    text-shadow: 0 0 10px #00ffff !important;
  }

  .scanlines {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      transparent 50%,
      rgba(0, 255, 0, 0.1) 50%
    );
    background-size: 100% 4px;
    animation: scanline 0.1s linear infinite;
    pointer-events: none;
  }

  .glitch {
    animation: glitch 0.3s ease-in-out infinite;
  }

  @keyframes neonPulse {
    0% { 
      box-shadow: 
        0 0 20px #00ff00,
        inset 0 0 20px rgba(0, 255, 0, 0.1);
    }
    100% { 
      box-shadow: 
        0 0 40px #00ff00,
        0 0 60px #00ff00,
        inset 0 0 30px rgba(0, 255, 0, 0.2);
    }
  }

  @keyframes textFlicker {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
      text-shadow: 
        0 0 5px #00ff00,
        0 0 10px #00ff00,
        0 0 20px #00ff00,
        0 0 40px #00ff00;
    }
    20%, 24%, 55% {
      text-shadow: none;
    }
  }

  @keyframes loadingAnimation {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes scanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }

  @keyframes glitch {
    0%, 100% { transform: translate(-50%, -50%); }
    20% { transform: translate(-50%, -48%); }
    40% { transform: translate(-52%, -50%); }
    60% { transform: translate(-48%, -52%); }
    80% { transform: translate(-50%, -48%); }
  }

  @media (max-width: 768px) {
    .neon-box {
      padding: 2rem 1rem;
      margin: 0 1rem;
    }
    
    .neon-text {
      font-size: 2.5rem;
    }
    
    .sub-text {
      font-size: 0.9rem;
    }
  }
</style> 